import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

// Import service images
import woodImage from "@/assets/service-wood.jpg";
import aluminumImage from "@/assets/service-aluminum.jpg";
import ironImage from "@/assets/service-iron.jpg";
import stainlessImage from "@/assets/service-stainless.jpg";
import glassImage from "@/assets/service-glass.jpg";
import marbleImage from "@/assets/service-marble.jpg";

export const Services = () => {
  const { language, isRTL } = useLanguage();

  const content = {
    ar: {
      badge: "ماذا نقدم",
      title: "خدماتنا المتميزة",
      subtitle: "نقدم مجموعة شاملة من الخدمات الصناعية عالية الجودة لتلبية جميع احتياجاتكم",
      viewDetails: "عرض التفاصيل",
      services: [
        {
          title: "صناعة الأخشاب",
          description: "تصميم وتنفيذ الأثاث المنزلي، المطابخ، الأبواب الخشبية، الديكورات، وتجاليد الجدران",
        },
        {
          title: "صناعة الألمنيوم",
          description: "أبواب ونوافذ ألمنيوم فخمة، مظلات وسواتر، واجهات زجاجية للمباني والأبراج",
        },
        {
          title: "أعمال الحديد",
          description: "هناجر، مظلات حديدية، أبواب أمنية، درابزينات، كرفانات ومكاتب متنقلة",
        },
        {
          title: "الاستانلس ستيل",
          description: "واجهات، درابزينات فاخرة، تجهيزات المطابخ الصناعية، ديكورات داخلية وخارجية",
        },
        {
          title: "الزجاج",
          description: "واجهات زجاجية، سيكوريت، قواطع مكتبية، زجاج عازل ومزخرف، مرايا بأنواعها",
        },
        {
          title: "صناعة الرخام",
          description: "أسطح رخام للمطابخ والحمامات، أرضيات، درج رخامي، واجهات، ترميم وصيانة",
        },
      ]
    },
    en: {
      badge: "What We Offer",
      title: "Our Premium Services",
      subtitle: "We offer a comprehensive range of high-quality industrial services to meet all your needs",
      viewDetails: "View Details",
      services: [
        {
          title: "Wood Manufacturing",
          description: "Design and execution of home furniture, kitchens, wooden doors, decorations, and wall cladding",
        },
        {
          title: "Aluminum Fabrication",
          description: "Premium aluminum doors and windows, shades and screens, glass facades for buildings and towers",
        },
        {
          title: "Iron Works",
          description: "Hangars, iron shades, security doors, railings, caravans and mobile offices",
        },
        {
          title: "Stainless Steel",
          description: "Facades, luxury railings, industrial kitchen equipment, interior and exterior decorations",
        },
        {
          title: "Glass Works",
          description: "Glass facades, tempered glass, office partitions, insulated and decorative glass, all types of mirrors",
        },
        {
          title: "Marble Works",
          description: "Marble surfaces for kitchens and bathrooms, flooring, marble stairs, facades, restoration and maintenance",
        },
      ]
    }
  };

  const t = content[language];

  const servicesMeta = [
    { color: "from-amber-600 to-amber-800", bgColor: "bg-amber-50", image: woodImage, link: "/wood" },
    { color: "from-gray-500 to-gray-700", bgColor: "bg-gray-50", image: aluminumImage, link: "/aluminum" },
    { color: "from-slate-600 to-slate-800", bgColor: "bg-slate-50", image: ironImage, link: "/iron" },
    { color: "from-zinc-500 to-zinc-700", bgColor: "bg-zinc-50", image: stainlessImage, link: "/stainless-steel" },
    { color: "from-blue-400 to-blue-600", bgColor: "bg-blue-50", image: glassImage, link: "/glass" },
    { color: "from-stone-500 to-stone-700", bgColor: "bg-stone-50", image: marbleImage, link: "/marble" },
  ];

  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  return (
    <section className="py-20 bg-muted/30" id="services" dir={isRTL ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">
            {t.badge}
          </span>
          <h2 className="text-3xl md:text-4xl font-kufi font-bold text-foreground mb-4">{t.title}</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {t.services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Link to={servicesMeta[index].link}>
                <Card className="group h-full border-none hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
                  <div className="relative h-48 overflow-hidden">
                    <img loading="lazy" decoding="async" 
                      src={servicesMeta[index].image} 
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${servicesMeta[index].color} opacity-40`} />
                  </div>
                  <CardContent className="p-6 bg-card">
                    <h3 className="text-xl font-kufi font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-muted-foreground font-kufi text-sm mb-4 leading-relaxed">
                      {service.description}
                    </p>
                    <div className="flex items-center gap-2 text-primary font-semibold text-sm group-hover:gap-3 transition-all">
                      <span>{t.viewDetails}</span>
                      <ArrowIcon className="h-4 w-4" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
